import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import Card from '../components/common/Card'
import Spinner from '../components/common/Spinner'
import ScoreSummary from '../components/player/ScoreSummary'
import WordFeedback from '../components/player/WordFeedback'
import ShareToFeed from '../components/social/ShareToFeed'
import { useAutoPost } from '../hooks/useFeatures'
import { faDigits } from '../lib/numerals'
import { classifyFailure, errorCode } from '../services/api'
import { getAttempt } from '../services/lessons'

/**
 * A result reopened from the archive (`attempts_detail`, B2): the same
 * score ring and word-by-word feedback the lesson page shows the moment a
 * take is scored — rebuilt on the server from the stored Azure response,
 * so it is the assessment the learner saw, not a second reading of it.
 *
 * No "sentence N of M": the lesson's length today is not its length the
 * day this was scored. No practice actions either — there is no take here
 * to redo and no next sentence to go to.
 */
export default function ArchiveResult() {
  const { t } = useTranslation('review')
  const autoPost = useAutoPost()
  const { attemptId } = useParams()
  const [detail, setDetail] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setDetail(null)
    setError(null)
    getAttempt(attemptId)
      .then((data) => {
        if (!cancelled) setDetail(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    return () => {
      cancelled = true
    }
  }, [attemptId])

  if (error) {
    const gone = errorCode(error) === 'not_found'
    const offline = classifyFailure(error) === 'offline'
    return (
      <div className="space-y-4">
        <BackLink />
        <div role="status" className={`rounded-xl p-3 text-sm ${offline ? 'bg-warn-tint text-warn-ink' : 'bg-bad-tint text-bad-ink'}`}>
          {gone
            ? t('result.notFound')
            : offline
              ? t('result.offline')
              : t('result.genericError')}
        </div>
      </div>
    )
  }

  if (!detail) return <Spinner label={t('result.loading')} />

  return (
    <div className="space-y-5">
      <BackLink />

      <Card>
        <p className="flex flex-wrap items-center gap-2 text-[12.5px] text-ink-2">
          <span className="lesson-text ltr">{detail.lessonTitleEn}</span>
          <span>{t('result.sentenceIndex', { index: faDigits(detail.sentenceIndex + 1) })}</span>
        </p>
        <p className="lesson-text ltr mt-3 rounded-xl bg-ground p-4 text-lg leading-relaxed text-ink">{detail.sentenceTextEn}</p>
      </Card>

      {detail.assessment ? (
        <>
          <Card title={t('result.resultTitle')}>
            <ScoreSummary assessment={detail.assessment} xpAwarded={detail.xpAwarded} aiXp={detail.aiXp} socialXp={detail.socialXp} />
          </Card>
          <Card title={t('result.wordFeedbackTitle')}>
            <WordFeedback words={detail.assessment.words ?? []} />
          </Card>
          {/* Share-to-feed for a scored take with a recording (P-core-4); the server refuses the rest by name.
              Owner, 2026-10-14: none while the server posts every scored take itself. */}
          {!autoPost && <ShareToFeed attemptId={detail.userAudioUrl ? detail.id : null} />}
        </>
      ) : (
        <Card>
          <p className="text-sm text-ink-2">
            {detail.status === 'failed'
              ? t('result.noAssessmentFailed')
              : t('result.noAssessmentPending')}
          </p>
          {detail.errorMessage && (
            <p className="ltr mt-2 text-xs text-ink-3">{detail.errorMessage}</p>
          )}
        </Card>
      )}
    </div>
  )
}

function BackLink() {
  const { t } = useTranslation('review')
  return (
    <Link to="/archive" className="inline-flex items-center gap-1.5 text-sm text-brand-700 hover:underline">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 6l6 6-6 6" />
      </svg>
      {t('result.backLink')}
    </Link>
  )
}
